// This is a TypeScript equivalent of security schema as described in "discovery" repository in file:
// src/worker/configuration/tasks/_helpers/security/schema.js
//
// Please keep them in sync.

// ----- VALUES -----

import type { CiscoMac } from '../types';
import type { NonEmptyArray } from '../types/NonEmptyArray';
import type { SD_WAN_PROTOCOL } from './PacketHeader';

export type NumberInterval = {
  min: bigint;
  max: bigint;
};

// NumberSet is already a name of class used in path-lookup, so the type here is called DbNumberSet,
// as it represents database serialization of NumberSet class in database.
export type DbNumberSet = NumberInterval[];

type StringInterval = {
  min: string;
  max: string;
};

export type DbStringSet = StringInterval[];

export type BitMaskCheck = {
  mask: number | bigint;
  result: number | bigint;
};

// Supported data types used in simple tests

type LiteralValueBase = {
  name?: string;
  category?: string;
  description?: string;
  original?: string;
};

// Reference to a named value in a dictionary has three components:
// + dictionary: 'local' | 'objectGroup'
// + type: 'boolean' | 'number' | 'numberArray' | 'numberSet' | 'string' | 'stringArray' | 'stringSet'
// + key: non-empty string

export type ReferenceBase = {
  dictionary: 'local' | 'objectGroup';
  key: string;
};

export type TestReference = ReferenceBase;

type ValueReference<ValueType> = ReferenceBase & {
  type: ValueType;
};

type LiteralBooleanValue = LiteralValueBase & {
  type: 'boolean';
  value: boolean;
};
type ReferencedBooleanValue = {
  reference: ValueReference<'boolean'>;
};
type BooleanValue = LiteralBooleanValue | ReferencedBooleanValue;

type LiteralStringValue = LiteralValueBase & {
  type: 'string';
  value: string;
};
type ReferencedStringValue = {
  reference: ValueReference<'string'>;
};
type StringValue = LiteralStringValue | ReferencedStringValue;

type LiteralStringArrayValue = LiteralValueBase & {
  type: 'stringArray';
  value: string[];
};
type ReferencedStringArrayValue = {
  reference: ValueReference<'stringArray'>;
};
type StringArrayValue = LiteralStringArrayValue | ReferencedStringArrayValue;

export type LiteralStringSetValue = LiteralValueBase & {
  original: string; // mandatory
  type: 'stringSet';
  value: DbStringSet;
};
export type ReferencedStringSetValue = {
  reference: ValueReference<'stringSet'>;
};
export type StringSetValue = LiteralStringSetValue | ReferencedStringSetValue;

export type LiteralStringSetGroupValue = LiteralValueBase & {
  type: 'stringSetGroup';
  value: StringSetGroupItem[];
};
export type ReferencedStringSetGroupValue = {
  reference: ValueReference<'stringSetGroup'>;
};
type StringSetGroupItem =
  | StringSetValue
  | LiteralStringSetGroupValue
  | ReferencedStringSetGroupValue;
export type StringSetGroupValue =
  | LiteralStringSetGroupValue
  | ReferencedStringSetGroupValue;

export type StringSetOrGroupValue = StringSetValue | StringSetGroupValue;

type LiteralNumberValue = LiteralValueBase & {
  type: 'number';
  value: number;
};
type ReferencedNumberValue = {
  reference: ValueReference<'number'>;
};
type NumberValue = LiteralNumberValue | ReferencedNumberValue;

type LiteralNumberArrayValue = LiteralValueBase & {
  type: 'numberArray';
  value: number[];
};
type ReferencedNumberArrayValue = {
  reference: ValueReference<'numberArray'>;
};
type NumberArrayValue = LiteralNumberArrayValue | ReferencedNumberArrayValue;

export type LiteralNumberSetValue = Omit<LiteralValueBase, 'original'> & {
  original: string; // mandatory
  type: 'numberSet';
  value: DbNumberSet;
};
export type ReferencedNumberSetValue = {
  reference: ValueReference<'numberSet'>;
};
export type NumberSetValue = LiteralNumberSetValue | ReferencedNumberSetValue;

export type LiteralNumberSetGroupValue = LiteralValueBase & {
  type: 'numberSetGroup';
  value: NumberSetGroupItem[];
};
export type ReferencedNumberSetGroupValue = {
  reference: ValueReference<'numberSetGroup'>;
};
type NumberSetGroupItem =
  | NumberSetValue
  | LiteralNumberSetGroupValue
  | ReferencedNumberSetGroupValue;
export type NumberSetGroupValue =
  | LiteralNumberSetGroupValue
  | ReferencedNumberSetGroupValue;
export function isLiteralNumberSetItem(
  item: NumberSetGroupItem,
): item is LiteralNumberSetValue {
  return 'type' in item && item.type === 'numberSet';
}

type LiteralBitMaskCheckValue = Omit<LiteralValueBase, 'original'> & {
  original: string; // mandatory
  type: 'bitMaskCheck';
  value: BitMaskCheck;
};
type ReferencedBitMaskCheckValue = {
  reference: ValueReference<'bitMaskCheck'>;
};
type BitMaskCheckValue = LiteralBitMaskCheckValue | ReferencedBitMaskCheckValue;

export type LiteralValue =
  | LiteralBitMaskCheckValue
  | LiteralBooleanValue
  | LiteralNumberArrayValue
  | LiteralNumberSetGroupValue
  | LiteralNumberSetValue
  | LiteralNumberValue
  | LiteralStringArrayValue
  | LiteralStringSetGroupValue
  | LiteralStringSetValue
  | LiteralStringValue;

export type ReferencedValue =
  | ReferencedBitMaskCheckValue
  | ReferencedBooleanValue
  | ReferencedNumberArrayValue
  | ReferencedNumberSetGroupValue
  | ReferencedNumberSetValue
  | ReferencedNumberValue
  | ReferencedStringArrayValue
  | ReferencedStringSetGroupValue
  | ReferencedStringSetValue
  | ReferencedStringValue;

export const isReferencedValue = (
  value?: Value | null,
): value is ReferencedValue =>
  !!value && Object.prototype.hasOwnProperty.call(value, 'reference');

// Dictionary of literal values (sub-directory per each data type).
export type NamedValuesDictionary = {
  bitMaskCheck?: Record<string, LiteralBitMaskCheckValue>;
  boolean?: Record<string, LiteralBooleanValue>;
  number?: Record<string, LiteralNumberValue>;
  numberArray?: Record<string, LiteralNumberArrayValue>;
  numberSet?: Record<string, LiteralNumberSetValue>;
  numberSetGroup?: Record<string, LiteralNumberSetGroupValue>;
  string?: Record<string, LiteralStringValue>;
  stringArray?: Record<string, LiteralStringArrayValue>;
  stringSet?: Record<string, LiteralStringSetValue>;
  stringSetGroup?: Record<string, LiteralStringSetGroupValue>;
};

export type Value =
  | BitMaskCheckValue
  | BooleanValue
  | NumberArrayValue
  | NumberSetGroupValue
  | NumberSetValue
  | NumberValue
  | StringArrayValue
  | StringSetGroupValue
  | StringSetValue
  | StringValue;

export type ValueType = LiteralValue['type'];

// ----- PACKET SELECTORS -----

type PacketSelectorInterface = {
  header: 'interface';
  field: 'in' | 'out';
};

type PacketSelectorInterfaceZone = {
  header: 'interface';
  field: 'inZone' | 'outZone';
};

type PacketSelectorInterfaceZonesFlag = {
  header: 'interface';
  field: 'intraZone' | 'interZone';
};

type PacketSelectorInterfaceVmNicId = {
  header: 'interface';
  field: 'toHostVmNicId' | 'fromHostVmNicId';
};

type PacketSelectorInterfaceVrf = {
  header: 'interface';
  field: 'vrf';
};

type PacketSelectorMacAddress = {
  header: 'eth';
  field: 'src' | 'dst';
};

type PacketSelectorEthVlan = {
  header: 'eth';
  field: 'vlan';
};

type PacketSelectorMplsLabel = {
  header: 'mpls';
  field: 'labelIn';
};

type PacketSelectorVxlan = {
  header: 'vxlan';
  field: 'srcGrp' | 'dstGrp' | 'vni';
};

type PacketSelectorVxlanGroupFlag = {
  header: 'vxlan';
  field: 'intraGrp' | 'interGrp';
};

type PacketSelectorIpAddresses = {
  header: 'ip';
  field: 'src' | 'dst';
};

type PacketSelectorIpRegion = {
  header: 'ip';
  field: 'srcRegion' | 'dstRegion';
};

// GCP-only, display-only in the cloud security tree (path lookup doesn't
// simulate these); see `cloudTreeOnlyIpFields` in `securityPacketHelpers.ts`.
type PacketSelectorIpFqdn = {
  header: 'ip';
  field: 'srcFqdn' | 'dstFqdn';
};

type PacketSelectorIpThreatIntelligence = {
  header: 'ip';
  field: 'srcThreatIntelligence' | 'dstThreatIntelligence';
};

type PacketSelectorIpv6Addresses = {
  // TODO: selector not implemented yet!, we don't support IPv6 headers as of now
  header: 'ipv6';
  field: 'src' | 'dst';
};

type PacketSelectorIpProtocol = {
  header: 'ip';
  field: 'protocol';
};

type PacketSelectorIpTtl = {
  header: 'ip';
  field: 'ttl';
};

type PacketSelectorIpv6HopLimit = {
  header: 'ipv6';
  field: 'hopLimit';
};

type PacketSelectorIpFragmentOffset = {
  header: 'ip';
  field: 'fragmentOffset';
};

type PacketSelectorIpService = {
  // TODO: selector not implemented yet!, we don't support service fields of IP header as of now
  header: 'ip';
  field: 'dscp' | 'precedence' | 'tos';
};

type PacketSelectorIpLength = {
  // TODO: selector not implemented yet!, we don't support total-length field of IP header as of now
  header: 'ip';
  field: 'packet-length';
};

type PacketSelectorIcmp = {
  // TODO: ICMPv6 not yet supported
  header: 'icmp' | 'icmpv6';
  field: 'type' | 'code';
};

type PacketSelectorL4Ports = {
  header: 'l4';
  field: 'src' | 'dst';
};

type PacketSelectorTcpPorts = {
  header: 'tcp';
  field: 'src' | 'dst';
};

type PacketSelectorTcpFlags = {
  header: 'tcp';
  field: 'flags';
};

type PacketSelectorUdpPorts = {
  header: 'udp';
  field: 'src' | 'dst';
};

type PacketSelectorL7Application = {
  header: 'l7';
  field: 'application';
};

type PacketSelectorL7Category = {
  header: 'l7';
  field: 'category';
};

type PacketSelectorL7Url = {
  header: 'l7';
  field: 'url';
};

type PacketSelectorOptionsTracked = {
  header: 'options';
  field: 'tracked';
};

// possible values: see ProcessingPhase type below
type PacketSelectorProcessingPhase = {
  header: 'processing';
  field: 'phase';
};

type PacketSelectorNatPreRoutingChainName = {
  header: 'natPreRouting';
  field: 'chainName';
};

type PacketSelectorNatPreRoutingRuleName = {
  header: 'natPreRouting';
  field: 'ruleName';
};

// Resolved from sdWanHeader.proto or ipSecTunnel annotation on the packet.
// Used by Meraki VPN-to-VPN firewall rules to match tunnel traffic.
type PacketSelectorRoutingSdWanProtocol = {
  header: 'routing';
  field: 'sdWanProtocol';
};

// Grouped by return types

type PacketSelectorBoolean =
  | PacketSelectorInterfaceZonesFlag
  | PacketSelectorOptionsTracked
  | PacketSelectorVxlanGroupFlag;

type PacketSelectorString =
  | PacketSelectorInterface
  | PacketSelectorInterfaceVmNicId
  | PacketSelectorInterfaceVrf
  | PacketSelectorIpFqdn
  | PacketSelectorIpRegion
  | PacketSelectorL7Application
  | PacketSelectorL7Category
  | PacketSelectorL7Url
  | PacketSelectorMacAddress
  | PacketSelectorProcessingPhase
  | PacketSelectorNatPreRoutingChainName
  | PacketSelectorNatPreRoutingRuleName
  | PacketSelectorRoutingSdWanProtocol;

type PacketSelectorStringArray =
  | PacketSelectorInterfaceZone
  | PacketSelectorIpThreatIntelligence
  | PacketSelectorTcpFlags;

type PacketSelectorStringSet = PacketSelectorIpv6Addresses;

type PacketSelectorNumber =
  | PacketSelectorEthVlan
  | PacketSelectorIcmp
  | PacketSelectorIpFragmentOffset
  | PacketSelectorIpLength
  | PacketSelectorIpProtocol
  | PacketSelectorIpService
  | PacketSelectorIpTtl
  | PacketSelectorIpv6HopLimit
  | PacketSelectorVxlan
  | PacketSelectorMplsLabel;

type PacketSelectorNumberSet =
  | PacketSelectorIpAddresses
  | PacketSelectorL4Ports
  | PacketSelectorTcpPorts
  | PacketSelectorUdpPorts;

export type SelectorType =
  | 'bitMaskCheck'
  | 'boolean'
  | 'number'
  | 'numberSetIndex'
  | 'string'
  | 'stringArray'
  | 'stringSetIndex';

export type PacketSelector =
  | PacketSelectorBoolean
  | PacketSelectorNumber
  | PacketSelectorNumberSet
  | PacketSelectorString
  | PacketSelectorStringArray
  | PacketSelectorStringSet;

// ----- TESTS -----

export type TestBase = {
  name?: string;
  category?: string;
  description?: string;
};

// Tests with boolean left values

type SimpleTestBaseBoolean = TestBase & {
  left: PacketSelectorBoolean;
};

type SimpleTestIsEqualToBoolean = SimpleTestBaseBoolean & {
  operator: 'boolean==';
  right: BooleanValue;
};

// Tests with string left values

type SimpleTestBaseString = TestBase & {
  left: PacketSelectorString;
};

type SimpleTestIsEqualToString = SimpleTestBaseString & {
  operator: 'str==';
  right: StringValue;
};

type SimpleTestIsNotEqualToString = SimpleTestBaseString & {
  operator: 'str!=';
  right: StringValue;
};

type SimpleTestIsInStringArray = SimpleTestBaseString & {
  operator: 'str-in-arr';
  right: StringArrayValue;
};

type SimpleTestInStringSetMac = SimpleTestBaseString & {
  operator: 'str-matches-mac';
  right: StringSetValue;
};

type SimpleTestMatchesRx = SimpleTestBaseString & {
  operator: 'str-matches-rx';
  right: StringValue;
};

type SimpleTestMatchesRxInArray = SimpleTestBaseString & {
  operator: 'str-matches-rx-arr';
  right: StringArrayValue;
};

type SimpleTestIsRxMatchingString = SimpleTestBaseString & {
  operator: 'rx-matching-str';
  right: StringValue;
};

type SimpleTestIsRxMatchingStringInArray = SimpleTestBaseString & {
  operator: 'rx-matching-str-arr';
  right: StringArrayValue;
};

// Tests with number left values

type SimpleTestBaseNumber = TestBase & {
  left: PacketSelectorNumber;
};

type SimpleTestIsEqualToNumber = SimpleTestBaseNumber & {
  operator: 'num==';
  right: NumberValue;
};

type SimpleTestIsNotEqualToNumber = SimpleTestBaseNumber & {
  operator: 'num!=';
  right: NumberValue;
};

type SimpleTestIsLessThanNumber = SimpleTestBaseNumber & {
  operator: 'num<';
  right: NumberValue;
};

type SimpleTestIsLessThanOrEqualToNumber = SimpleTestBaseNumber & {
  operator: 'num<=';
  right: NumberValue;
};

type SimpleTestIsGreaterThanNumber = SimpleTestBaseNumber & {
  operator: 'num>';
  right: NumberValue;
};

type SimpleTestIsGreaterThanOrEqualToNumber = SimpleTestBaseNumber & {
  operator: 'num>=';
  right: NumberValue;
};

type SimpleTestIsInNumberArray = SimpleTestBaseNumber & {
  operator: 'num-in-arr';
  right: NumberArrayValue;
};

type SimpleTestIsInNumberSet = SimpleTestBaseNumber & {
  operator: 'num-in-set';
  right: NumberSetValue;
};

type SimpleTestBitAndEqualToNumber = SimpleTestBaseNumber & {
  operator: 'num-and-eq';
  right: BitMaskCheckValue;
};

// Tests with string array left values

type SimpleTestBaseStringArray = TestBase & {
  left: PacketSelectorStringArray;
};

type SimpleTestContainsString = SimpleTestBaseStringArray & {
  operator: 'arr-contains-str';
  right: StringValue;
};

type SimpleTestContainsAnyString = SimpleTestBaseStringArray & {
  operator: 'arr-contains-any-str';
  right: StringArrayValue;
};

type SimpleTestContainsAllString = SimpleTestBaseStringArray & {
  operator: 'arr-contains-all-str';
  right: StringArrayValue;
};

// Tests with number set left values

type SimpleTestBaseNumberSet = TestBase & {
  left: PacketSelectorNumberSet;
};

type SimpleTestIsEqualToNumberExt = SimpleTestBaseNumberSet & {
  operator: 'numset==';
  right: NumberValue;
};

type SimpleTestIsNotEqualToNumberExt = SimpleTestBaseNumberSet & {
  operator: 'numset!=';
  right: NumberValue;
};

type SimpleTestIsLessThanNumberExt = SimpleTestBaseNumberSet & {
  operator: 'numset<';
  right: NumberValue;
};

type SimpleTestIsLessThanOrEqualToNumberExt = SimpleTestBaseNumberSet & {
  operator: 'numset<=';
  right: NumberValue;
};

type SimpleTestIsGreaterThanNumberExt = SimpleTestBaseNumberSet & {
  operator: 'numset>';
  right: NumberValue;
};

type SimpleTestIsGreaterThanOrEqualToNumberExt = SimpleTestBaseNumberSet & {
  operator: 'numset>=';
  right: NumberValue;
};

type SimpleTestIsInNumberArrayExt = SimpleTestBaseNumberSet & {
  operator: 'numset-in-arr';
  right: NumberArrayValue;
};

type SimpleTestMatchesNumberSet = SimpleTestBaseNumberSet & {
  operator: 'numset-matches';
  right: NumberSetValue;
};

type SimpleTestNotEqualToNumberSet = SimpleTestBaseNumberSet & {
  operator: 'numset-not-matches';
  right: NumberSetValue;
};

type SimpleTestBitAndEqualToNumberExt = SimpleTestBaseNumberSet & {
  operator: 'numset-and-eq';
  right: BitMaskCheckValue;
};

// Tests with string set left values

type SimpleTestBaseStringSet = TestBase & {
  left: PacketSelectorStringSet;
};

type SimpleTestIsEqualToStringExt = SimpleTestBaseStringSet & {
  operator: 'strset==';
  right: StringValue;
};

type SimpleTestIsNotEqualToStringExt = SimpleTestBaseStringSet & {
  operator: 'strset!=';
  right: StringValue;
};

type SimpleTestIsLessThanStringExt = SimpleTestBaseStringSet & {
  operator: 'strset<';
  right: StringValue;
};

type SimpleTestIsLessThanOrEqualToStringExt = SimpleTestBaseStringSet & {
  operator: 'strset<=';
  right: StringValue;
};

type SimpleTestIsGreaterThanStringExt = SimpleTestBaseStringSet & {
  operator: 'strset>';
  right: StringValue;
};

type SimpleTestIsGreaterThanOrEqualToStringExt = SimpleTestBaseStringSet & {
  operator: 'strset>=';
  right: StringValue;
};

type SimpleTestIsInStringArrayExt = SimpleTestBaseStringSet & {
  operator: 'strset-in-arr';
  right: StringArrayValue;
};

type SimpleTestMatchesStringSet = SimpleTestBaseStringSet & {
  operator: 'strset-matches';
  right: StringSetValue;
};

type SimpleTestNotMatchesStringSet = SimpleTestBaseStringSet & {
  operator: 'strset-not-matches';
  right: StringSetValue;
};

type SimpleTestBitAndEqualToStringExt = SimpleTestBaseStringSet & {
  operator: 'strset-and-eq';
  right: BitMaskCheckValue;
};

// Trivial tests

export type TrivialTest = TestBase & {
  operator: 'true' | 'false';
  original?: string;
};

export const isTrivialTest = (test?: Test | null): test is TrivialTest =>
  isSimpleTest(test) && ['true', 'false'].includes(test.operator);

// All simple tests

// boolean
export type SimpleBooleanTest = SimpleTestIsEqualToBoolean;

// string
export type SimpleStringTest =
  | SimpleTestIsEqualToString
  | SimpleTestIsInStringArray
  | SimpleTestIsNotEqualToString
  | SimpleTestInStringSetMac
  | SimpleTestMatchesRx
  | SimpleTestMatchesRxInArray
  | SimpleTestIsRxMatchingString
  | SimpleTestIsRxMatchingStringInArray;

// string array
export type SimpleStringArrayTest =
  | SimpleTestContainsAllString
  | SimpleTestContainsAnyString
  | SimpleTestContainsString;

// string set
export type SimpleStringSetTest =
  | SimpleTestIsEqualToStringExt
  | SimpleTestIsGreaterThanOrEqualToStringExt
  | SimpleTestIsGreaterThanStringExt
  | SimpleTestIsInStringArrayExt
  | SimpleTestIsLessThanOrEqualToStringExt
  | SimpleTestIsLessThanStringExt
  | SimpleTestIsNotEqualToStringExt
  | SimpleTestMatchesStringSet
  | SimpleTestNotMatchesStringSet
  | SimpleTestBitAndEqualToStringExt;

// number
export type SimpleNumberTest =
  | SimpleTestBitAndEqualToNumber
  | SimpleTestIsEqualToNumber
  | SimpleTestIsGreaterThanNumber
  | SimpleTestIsGreaterThanOrEqualToNumber
  | SimpleTestIsInNumberArray
  | SimpleTestIsInNumberSet
  | SimpleTestIsLessThanNumber
  | SimpleTestIsLessThanOrEqualToNumber
  | SimpleTestIsNotEqualToNumber;

// number set
export type SimpleNumberSetTest =
  | SimpleTestBitAndEqualToNumberExt
  | SimpleTestIsEqualToNumberExt
  | SimpleTestIsGreaterThanNumberExt
  | SimpleTestIsGreaterThanOrEqualToNumberExt
  | SimpleTestIsInNumberArrayExt
  | SimpleTestIsLessThanNumberExt
  | SimpleTestIsLessThanOrEqualToNumberExt
  | SimpleTestIsNotEqualToNumberExt
  | SimpleTestMatchesNumberSet
  | SimpleTestNotEqualToNumberSet;

export type ComparisonTest =
  | SimpleBooleanTest
  | SimpleNumberSetTest
  | SimpleNumberTest
  | SimpleStringArrayTest
  | SimpleStringSetTest
  | SimpleStringTest;

export const isComparisonTest = (test?: Test | null): test is ComparisonTest =>
  isSimpleTest(test) && !isTrivialTest(test);

// all test types
export type SimpleTest = ComparisonTest | TrivialTest;

// Reference to a named test in a dictionary has two components:
// + dictionary: 'local' | 'objectGroup'
// + key: non-empty string

export type ReferencedTest = TestBase & {
  reference: TestReference;
};

export type EvalTest = TestBase & {
  chainName: string;
  chainType: SecurityType;
  checkType: 'passing' | 'failing';
};

export type NotGroupTest = TestBase & {
  group: 'not';
  item: Test;
};

export type AndGroupTest = TestBase & {
  group: 'and';
  items: Test[]; // 2+
};

export type OrGroupTest = TestBase & {
  group: 'or';
  items: Test[]; // 2+
};

export type Test =
  | AndGroupTest
  | NotGroupTest
  | OrGroupTest
  | ReferencedTest
  | SimpleTest
  | EvalTest;

export const isAndGroupTest = (test?: Test | null): test is AndGroupTest =>
  (test as AndGroupTest)?.group === 'and';

export const isNotGroupTest = (test?: Test | null): test is NotGroupTest =>
  (test as NotGroupTest)?.group === 'not';

export const isOrGroupTest = (test?: Test | null): test is OrGroupTest =>
  (test as OrGroupTest)?.group === 'or';

export const isReferencedTest = (test?: Test | null): test is ReferencedTest =>
  (test as ReferencedTest)?.reference !== undefined;

export const isEvalTest = (test?: Test | null): test is EvalTest =>
  (test as EvalTest)?.chainName !== undefined;

export const isSimpleTest = (test?: Test | null): test is SimpleTest =>
  (test as SimpleTest)?.operator !== undefined;

export type NamedTestsDictionary = Record<string, Test>;

// ----- ACTIONS -----

type NatIpv4OptionBase = {
  original?: string;
};

export type NumberSetOrGroupValue = NumberSetValue | NumberSetGroupValue;

export type NatIpv4OptionTranslatedCommon = NatIpv4OptionBase & {
  type: 'natIpv4Translated';
  header: 'ip' | 'tcp' | 'udp' | 'l4';
  field: 'src' | 'dst';
  value: NumberSetOrGroupValue;
};

export type NatIpv4OptionTranslatedStatic = NatIpv4OptionTranslatedCommon & {
  matchedValue?: NumberSetOrGroupValue;
};

export type NatIpv4OptionTranslatedDynamic = NatIpv4OptionTranslatedCommon & {
  isDynamic: true;
};

export type NatIpv4OptionTranslated =
  | NatIpv4OptionTranslatedStatic
  | NatIpv4OptionTranslatedDynamic;

export type NatIpv4OptionL3IntPrimaryIp = NatIpv4OptionBase & {
  type: 'natIpv4L3IntPrimaryIp';
  header: 'ip';
  field: 'src';
  value: string;
};

export type NatIpv4OptionRoutingOutL3IntPrimaryIp = NatIpv4OptionBase & {
  type: 'natIpv4RoutingOutL3IntPrimaryIp';
  header: 'ip';
  field: 'src';
};

export type NatIpv4LoadBalancingTarget = {
  toIp: NumberSetValue;
  toPort?: NumberSetValue;
};

export type NatIpv4OptionLoadBalancingDnat = NatIpv4OptionBase & {
  type: 'natIpv4LoadBalancingDnat';
  value: [NatIpv4LoadBalancingTarget, ...NatIpv4LoadBalancingTarget[]]; // at least 1 item
};

type NatIpv6OptionBase = {
  original?: string;
};

export type Nat46StatelessToPrefixOption = {
  type: 'nat46StatelessToPrefix';
  header: 'ipv6';
  field: 'src' | 'dst';
  value: string;
  original?: string;
};

export type Nat64StatelessDstFromPrefixOption = {
  type: 'nat64StatelessDstFromPrefix';
  header: 'ip';
  field: 'dst';
  original?: string;
};

export type NatIpv6OptionTranslatedCommon = NatIpv6OptionBase & {
  type: 'natIpv6Translated';
  header: 'ipv6' | 'tcp' | 'udp' | 'l4';
  field: 'src' | 'dst';
  value: StringSetOrGroupValue;
};

export type NatIpv6OptionTranslatedStatic = NatIpv6OptionTranslatedCommon & {
  matchedValue?: StringSetOrGroupValue;
};

export type NatIpv6OptionTranslatedDynamic = NatIpv6OptionTranslatedCommon & {
  isDynamic: true;
};

export type NatIpv6OptionTranslated =
  | NatIpv6OptionTranslatedStatic
  | NatIpv6OptionTranslatedDynamic;

export type NatIpv6OptionL3IntPrimaryIp = NatIpv6OptionBase & {
  type: 'natIpv6L3IntPrimaryIp';
  header: 'ipv6';
  field: 'src';
  value: string;
};

export type NatIpv6OptionRoutingOutL3IntPrimaryIp = NatIpv6OptionBase & {
  type: 'natIpv6RoutingOutL3IntPrimaryIp';
  header: 'ipv6';
  field: 'src';
};

export type PbrIpOptionType =
  // PBR interface can be both IPv4 and IPv6
  | 'pbrInterface'
  | 'pbrInterfaceDefault'
  | 'pbrIpv4Nexthops'
  | 'pbrIpv4NexthopsDefault'
  | 'pbrIpv4NexthopsRecursive'
  | 'pbrIpv4NexthopsVerifyAvailability';

export type PbrMacOptionType = 'pbrMacNexthops';

export type Nexthop = {
  ip?: string;
  ipLong?: number;
  int?: string;
  ad?: number;
  metric?: number;
  age?: number;
  vrfLeak?: string;
  labelStack?: number[];
  oid?: string;
  oidOriginal?: string;
  sdWanHeader?: {
    proto: SD_WAN_PROTOCOL;
    vrfName: string;
  };
  vni?: number;
  vtepIp?: string;
  vtepIpLong?: number;
};

export type PbrIpOption = {
  type: PbrIpOptionType;
  original: string;
  nexthopList: Nexthop[];
  popMplsLabel?: true;
};

export type PbrMacNexthop = {
  mac: CiscoMac;
  vlan: number;
};

export type PbrMacOption = {
  type: 'pbrMacNexthops';
  original: string;
  nexthopList: PbrMacNexthop[];
};

export type PbrVrfOption = {
  type: 'pbrVrf';
  original?: string;
  value: string;
};

export type PathLookupOption =
  | NatIpv4OptionTranslated
  | NatIpv4OptionL3IntPrimaryIp
  | NatIpv4OptionRoutingOutL3IntPrimaryIp
  | NatIpv4OptionLoadBalancingDnat
  | Nat46StatelessToPrefixOption
  | Nat64StatelessDstFromPrefixOption
  | NatIpv6OptionTranslated
  | NatIpv6OptionL3IntPrimaryIp
  | NatIpv6OptionRoutingOutL3IntPrimaryIp
  | PbrIpOption
  | PbrMacOption
  | PbrVrfOption;

type ActionBase = {
  original?: string;
};

type ActionContinue = ActionBase & {
  type: 'continue';
};

type ActionExec = ActionBase & {
  chainName: string;
  type: 'exec';
};

type ActionReturn = ActionBase & {
  type: 'return';
};

type ActionTerminating = ActionBase & {
  options?: Record<string, unknown>;
  type: 'allow' | 'deny';
};

export type ActionWithOptions = ActionBase & {
  type: 'allow';
  pathLookupOptions: PathLookupOption[];
};

export const isActionWithOptions = (
  action: Action,
): action is ActionWithOptions =>
  Object.hasOwnProperty.call(action, 'pathLookupOptions');

export type Action =
  | ActionContinue
  | ActionExec
  | ActionReturn
  | ActionTerminating
  | ActionWithOptions;

// ----- RULES -----

// Kind of cloud policy a rule was authored in; `vCloudSecurity.type`
// normalized per vendor.
export type CloudPolicyKind =
  | 'securityGroup'
  | 'nacl'
  | 'nsg'
  | 'gcpVpcFirewall'
  | 'gcpFirewallPolicy';

// Owner of a cloud rule, stamped by the path-lookup security resolver when it
// merges several policies into one machine (an ENI stacks SGs) so the deciding
// policy stays nameable. Never emitted by discovery.
export type CloudPolicyRef = {
  id: string;
  kind: CloudPolicyKind;
  name?: string;
};

export type Rule = {
  action: Action;
  active: boolean;
  cloudPolicy?: CloudPolicyRef;
  description?: string;
  name?: string;
  natEvaluationType?: 'auto' | 'manual';
  // Cloud first-match ordering (NSG / NACL rule number); mirrors discovery's
  // optional `priority`. On-prem chains keep relying on array order.
  priority?: number;
  sequenceNumber?: number; // assert than it is >= 0
  test: Test;
  uid?: string | null;
};

export type RuleChain = {
  defaultAction: Action;
  hideInUi?: true;
  isEntryChain?: true;
  name: string;
  rules: Rule[]; // can be empty
};

// ----- RESULTING (RUN-TIME) SECURITY SCHEMA -----

export type ReferenceDictionaries = {
  namedTests?: NamedTestsDictionary;
  namedValues?: NamedValuesDictionary;
};

export type SecurityMachine = ReferenceDictionaries & {
  entryChainName: string; // name of ruleChains element that has isEntryChain flag set to true
  ruleChains: { [chainName: string]: RuleChain };
};

type SecurityGroupObjectSchema = ReferenceDictionaries;

export type InterfacesZones = {
  [ifaceName: string]: string[];
};

export type DeviceSecurity = {
  machineAcl: SecurityMachine | null;
  machineNat44: SecurityMachine | null;
  machinePbr: SecurityMachine | null;
  machineZones: SecurityMachine | null;
  // Cloud NIC / subnet security (SG / NACL / NSG), assembled at query time by
  // the path-lookup engine rather than read from a discovery collection.
  machineCloud: SecurityMachine | null;
  objects?: SecurityGroupObjectSchema;
  zones?: InterfacesZones;
};

// 'cloud' covers cloud NIC (ingress/egress) and subnet enforcement points; the
// enforcing simulation construct and the traffic direction select the rules,
// so a single type suffices (no separate per-direction / per-scope types).
export type SecurityType = 'acl' | 'cloud' | 'nat44' | 'pbr' | 'zoneFw';

export type ProcessingPhase =
  | 'input'
  | 'forward'
  | 'output'
  | 'vmnic-from-host'
  | 'vmnic-to-host'
  | 'natPreRouting'
  | 'natPostRouting'
  | 'pbrPreRouting';

export type DeviceDescription = {
  activeSecurity: boolean;
  devType?: string;
  enforceSecurity: boolean;
  stopIfaceNames?: Set<string>;
  versionDetail?: {
    family?: string;
    vendor?: string;
    aciRole?: 'leaf' | 'spine';
  };
  sn?: string; // serial number
  [key: string]: unknown; // whatever we get from vDevice collection for given id
};

export type DeviceSecurityOptions = {
  deviceDescription: DeviceDescription;
  phase: ProcessingPhase;
  scopeIpProtocol?: 'tcp' | 'udp';
  type: SecurityType;
  virtual?: boolean;
};

/**
 * Path of rule in a security machine
 *
 * Let SM be an instance of SecurityMachine.
 * First element is index in SM.ruleChains[SM.entryChainName].rules.
 * If that index refers to a rule with action "A" of type ActionExec,
 * next element in RulePath array is index of SM[A.chainName].rules.
 *
 * Only the last index points to action type other than ActionExec.
 *
 * Note: when any index on the path is equal to ruleChain.rules.length
 * (it points after the last element), it means default action of given
 * rule chain, which we consider to be a special rule.
 */
export type RulePath = NonEmptyArray<number>;

// NAT|PBR supporting types
export type LiteralNatIpv4OptionTranslatedCommon =
  NatIpv4OptionTranslatedCommon & {
    value: LiteralNumberSetValue;
  };

export type LiteralNatIpv4OptionTranslatedStatic =
  LiteralNatIpv4OptionTranslatedCommon & {
    isDynamic: false;
    matchedValue: LiteralNumberSetValue;
  };

export type LiteralNatIpv4OptionTranslatedDynamic =
  LiteralNatIpv4OptionTranslatedCommon & {
    isDynamic: true;
  };

export type LiteralNatIpv4OptionTranslated =
  | LiteralNatIpv4OptionTranslatedStatic
  | LiteralNatIpv4OptionTranslatedDynamic;

export type LiteralNatIpv4LoadBalancingTarget = {
  toIp: LiteralNumberSetValue;
  toPort?: LiteralNumberSetValue;
};

export type AtLeastOneLiteralLoadBalancingTarget = [
  LiteralNatIpv4LoadBalancingTarget,
  ...LiteralNatIpv4LoadBalancingTarget[],
];

// Omit is needed because of the fancy "at least 1" [Target, ...Target[]] type.
export type LiteralNatIpv4OptionLoadBalancingDnat = Omit<
  NatIpv4OptionLoadBalancingDnat,
  'value'
> & {
  value: AtLeastOneLiteralLoadBalancingTarget;
};

// Helper to create a LiteralNatIpv4OptionLoadBalancingDnat so we do not have to cast it with "as".
export function createLiteralLoadBalancingDnat(
  targets: AtLeastOneLiteralLoadBalancingTarget,
): LiteralNatIpv4OptionLoadBalancingDnat {
  return { type: 'natIpv4LoadBalancingDnat', value: targets };
}

export function targetsHaveAtLeastOneElement(
  targets: LiteralNatIpv4LoadBalancingTarget[],
): targets is AtLeastOneLiteralLoadBalancingTarget {
  return targets.length >= 1;
}

export function isDynamicTranslatedNat(
  option: NatIpv4OptionTranslated,
): option is NatIpv4OptionTranslatedDynamic {
  const hasDynamicFlag = Object.prototype.hasOwnProperty.call(
    option,
    'isDynamic',
  );
  if (!hasDynamicFlag) {
    return false;
  }
  if (!(option as NatIpv4OptionTranslatedDynamic).isDynamic) {
    return false;
  }
  return true;
}

export type LiteralNatPathLookupOption =
  | LiteralNatIpv4OptionTranslated
  | NatIpv4OptionL3IntPrimaryIp
  | NatIpv4OptionRoutingOutL3IntPrimaryIp
  | LiteralNatIpv4OptionLoadBalancingDnat;

export type LiteralPbrIpPathLookupOption = PbrIpOption;
export type LiteralPbrMacPathLookupOption = PbrMacOption;
export type LiteralPbrVrfPathLookupOption = PbrVrfOption;
export type LiteralPbrPathLookupOption =
  | LiteralPbrIpPathLookupOption
  | LiteralPbrMacPathLookupOption
  | LiteralPbrVrfPathLookupOption;

export type LiteralPathLookupOption =
  | LiteralNatPathLookupOption
  | LiteralPbrPathLookupOption;

const pbrTypes = [
  'pbrInterface',
  'pbrInterfaceDefault',
  'pbrIpv4Nexthops',
  'pbrIpv4NexthopsDefault',
  'pbrIpv4NexthopsRecursive',
  'pbrIpv4NexthopsVerifyAvailability',
];

export function isPbrIpOption(
  input: PathLookupOption,
): input is LiteralPbrIpPathLookupOption {
  return pbrTypes.includes(input.type);
}

export function isPbrMacOption(
  input: PathLookupOption,
): input is LiteralPbrMacPathLookupOption {
  return input.type === 'pbrMacNexthops';
}

export function isPbrVrfOption(
  input: PathLookupOption,
): input is LiteralPbrVrfPathLookupOption {
  return input.type === 'pbrVrf';
}

export const isPbrOptions = (
  input: LiteralPathLookupOption[],
): input is LiteralPbrPathLookupOption[] =>
  input.every(
    (op) => isPbrIpOption(op) || isPbrMacOption(op) || isPbrVrfOption(op),
  );

type NatPathLookupOption =
  | NatIpv4OptionTranslated
  | NatIpv4OptionL3IntPrimaryIp
  | NatIpv4OptionRoutingOutL3IntPrimaryIp
  | NatIpv4OptionLoadBalancingDnat;

const natTypes = [
  'natIpv4Translated',
  'natIpv4L3IntPrimaryIp',
  'natIpv4RoutingOutL3IntPrimaryIp',
  'natIpv4LoadBalancingDnat',
];

export function isNatIpv4Option(
  input: PathLookupOption,
): input is NatPathLookupOption {
  return natTypes.includes(input.type);
}

export const isNatOptions = (
  input: LiteralPathLookupOption[],
): input is LiteralNatPathLookupOption[] =>
  input.every((op) => isNatIpv4Option(op));
